import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useInventory } from "../../hooks/useInventory";
import { useItems } from "../../hooks/useItems";
import { useUnits } from "../../hooks/useUnits";
import { useAuth } from "../../hooks/useAuth";
import { usePersonnel } from "../../hooks/usePersonnel";
import { useUsers } from "../../hooks/useUsers";
import { useInventoryCalculations } from "../../hooks/useInventoryCalculations";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useVirtualScroll } from "../../hooks/useVirtualScroll";
import { FaCamera } from "react-icons/fa";
import InventoryForm from "../../components/forms/InventoryForm";
import ImageScanModal from "../Signature/components/ImageScanModal";
import VirtualInventoryTable from "../../components/VirtualInventoryTable";
import { ClickableInventoryItemName } from "../../components/ClickableInventoryItemName";
import MobileInventoryCard from "./components/MobileInventoryCard";
import RefundModal from "./components/RefundModal";
import InventoryUnitMultiSelect from "./components/InventoryUnitMultiSelect";
import {
  childHasFirstAllocation,
  getParent9263FirestoreId,
  getUnitIdForAssigneeKey,
  itemBelongsToChildUnit,
  itemMatchesUnitFilterSelection,
} from "./utils/inventoryUnitFilter";
import {
  buildSerialGroupedInventoryRows,
  getSerialInventoryGroupKey,
} from "./utils/buildSerialGroupedInventoryRows";
import { mergeAssignmentsFirstFromMembers } from "./utils/mergeSerialGroupAssignmentsFirst";
import {
  buildNicknameByPersonalNumberMap,
  buildAssigneeSearchHaystack,
  formatAssignedPersonnelList,
  formatPersonDisplayName,
  findPersonnelById,
} from "../../utils/personnelDisplay";
import {
  FaEdit,
  FaArrowLeft,
  FaArrowRight,
  FaTrash,
  FaEye,
  FaCog,
  FaSms,
  FaFileExcel,
  FaLayerGroup,
  FaList,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import {
  getSeenStatus,
  shouldClearSeenOnEyeClick,
} from "../../utils/validation";
import { getAssignmentUrl } from "../../config/urls";
import {
  sendTsavReportSms,
  getTsavDailyUsageShared,
  incrementTsavDailyUsageShared,
} from "../../services/smsService";
import { getOrCreatePageId } from "../../utils/employeePages";
import { normalizeItemSearchText } from "../../utils/itemSearchText";
import "./Inventory.css";
import "./MobileInventory.css";

// Constants for better code organization
const ITEMS_PER_PAGE_BASE = 20; // Base items per page for small screens
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_SORT_FIELD = "createdAt";
const DEFAULT_SORT_DIRECTION = "desc";
const VIRTUAL_SCROLL_THRESHOLD = 100; // Use virtual scrolling for 100+ items
const ROW_HEIGHT = 60; // Approximate height of each table row
const HEADER_AND_CONTROLS_HEIGHT = 300; // Approximate height of header, stats, controls (reduced to minimize bottom space)

const STATUS_FILTERS = {
  ALL: "all",
  AVAILABLE: "available",
  ASSIGNED: "assigned",
  RETURNED: "returned",
  NOT_SEEN: "not_seen",
  SERIAL_ONLY: "serial_only",
};

/**
 * "דוח צ פריטים שלא סומנו": סידורי, לא זוכה — רק שלא דווחו (אין seenAt) או `seen-red`
 * לפי getSeenStatus(seenAt, greenMax, yellowDays) מההגדרות; לא כולל צהוב (יום yellowDays בדיוק).
 */
function itemMatchesNotSeenSerialFilter(item, seenSettings) {
  const greenMax = seenSettings?.greenMax ?? 2;
  const yellowDays = seenSettings?.yellowDays ?? 3;
  const isSerial =
    item.serialNumber && String(item.serialNumber).trim() !== "";
  if (!isSerial) return false;
  if (item.calculatedStatus === "returned") return false;
  if (!item.seenAt) return true;
  const seenStatus = getSeenStatus(item.seenAt, greenMax, yellowDays);
  return seenStatus?.colorClass === "seen-red";
}

/** Days since דוח צ' for sorting: 0 = today; null / no serial → +Infinity (sorts last on asc). */
const getSeenAtSortKey = (item, greenMax, yellowDays) => {
  const isSerial =
    item.serialNumber && String(item.serialNumber).trim() !== "";
  if (!isSerial) return Number.POSITIVE_INFINITY;
  const { days } = getSeenStatus(item.seenAt, greenMax, yellowDays);
  return days != null ? days : Number.POSITIVE_INFINITY;
};

/** מיון חתום ל — מצב שני: זמינים / ללא חתום לפני אלפבית */
const ASSIGNED_TO_SORT_AVAIL = "avail";

function getAssignedToSortLabel(item, nicknameByPersonalNumber, personnel) {
  const isSerial =
    item.serialNumber && String(item.serialNumber).trim() !== "";
  if (!isSerial) return "";
  const hasAssignee =
    (item.calculatedStatus === "assigned" || item.status === "assigned") &&
    item.assignedPersonnel?.length > 0;
  if (!hasAssignee) return "";
  return formatAssignedPersonnelList(
    item.assignedPersonnel,
    nicknameByPersonalNumber,
    personnel,
  )
    .toLowerCase();
}

function compareAssignedToSort(
  a,
  b,
  sortDirection,
  nicknameByPersonalNumber,
  personnel,
) {
  const labelA = getAssignedToSortLabel(a, nicknameByPersonalNumber, personnel);
  const labelB = getAssignedToSortLabel(b, nicknameByPersonalNumber, personnel);
  const emptyA = !labelA;
  const emptyB = !labelB;

  if (sortDirection === ASSIGNED_TO_SORT_AVAIL) {
    if (emptyA !== emptyB) return emptyA ? -1 : 1;
    return labelA.localeCompare(labelB, "he");
  }

  const keyA = emptyA ? "\uffff" : labelA;
  const keyB = emptyB ? "\uffff" : labelB;
  const cmp = keyA.localeCompare(keyB, "he");
  if (sortDirection === "desc") return -cmp;
  return cmp;
}

function resolveUnitNameFromUnits(unitId, units) {
  if (!unitId || !units?.length) return "";
  const u = units.find((x) => x.id === unitId);
  return u ? String(u.unitName || "") : "";
}

/** Display text for יחידה column — must match VirtualInventoryTable logic. */
function getInventoryUnitColumnSortKey(
  item,
  personnelUnitMap,
  units,
  userRole,
  childUserUnitId,
) {
  if (userRole === "childUser" && childUserUnitId) {
    const serial =
      item.serialNumber && String(item.serialNumber).trim() !== "";
    if (serial) {
      return resolveUnitNameFromUnits(childUserUnitId, units).toLowerCase();
    }
  }
  if (
    item.assignedPersonnel?.length > 0 &&
    (item.calculatedStatus === "assigned" || item.status === "assigned")
  ) {
    const pn = item.assignedPersonnel[0].personalNumber;
    const employeeUnitId = getUnitIdForAssigneeKey(personnelUnitMap, pn);
    return employeeUnitId
      ? resolveUnitNameFromUnits(employeeUnitId, units).toLowerCase()
      : "";
  }
  return resolveUnitNameFromUnits(item.currentUnitId, units).toLowerCase();
}

/** Comma-separated serial numbers assigned to `personalNumber` across member rows (distribution modal). */
function formatDistributionSerialsForAssignee(memberRows, personalNumber) {
  if (!memberRows?.length || personalNumber == null || personalNumber === "")
    return "";
  const pn = String(personalNumber).trim();
  const list = [];
  for (const m of memberRows) {
    const sn = m.serialNumber && String(m.serialNumber).trim();
    if (!sn) continue;
    const qty = Number(m.assignments?.first?.[pn]?.quantity) || 0;
    if (qty > 0) list.push(sn);
  }
  list.sort((a, b) => a.localeCompare(b, "he", { sensitivity: "base" }));
  return list.length ? list.join(", ") : "";
}

/**
 * Inventory Component
 *
 * Mobile Optimization Strategy:
 * - Uses useIsMobile hook for conditional rendering (only mobile code runs on mobile)
 * - Mobile cards are extracted to separate component (MobileInventoryCard) for:
 *   - Better code organization and readability
 *   - React.memo optimization to prevent unnecessary re-renders
 *   - useMemo for computed values (assignedTo, seenStatus, etc.)
 * - Desktop table remains unchanged (no performance impact)
 * - Mobile code is completely isolated - doesn't affect desktop rendering
 */
const Inventory = () => {
  // Optimized hook usage - extract only needed functions
  const {
    inventory,
    loading,
    error,
    addInventoryItem,
    updateInventoryItem,
    returnItem,
    makeItemAvailable,
    deleteInventoryItem,
    updateSeenAt,
    formatFirestoreDate,
    refreshInventory,
    updateAssignmentNote,
  } = useInventory();

  const { items: itemsSettings } = useItems();
  const { units } = useUnits();
  const { personnel } = usePersonnel();
  const { user } = useAuth();
  const { users: appUsers } = useUsers();
  // Mobile detection - optimized hook with debouncing
  // Only mobile components render when true, zero desktop performance impact
  const isMobile = useIsMobile();

  // Get user role for calculations
  const userPermissions = user?.permissions || "childUser";
  const userRole =
    userPermissions === "admin"
      ? "admin"
      : userPermissions === "parentsUser"
        ? "parentsUser"
        : "childUser";
  const isAdmin = userPermissions === "admin";

  // Optimized childUser check - single source of truth
  const isChildUser = userRole === "childUser";

  // Get childUser's personal number and unit ID (combined for efficiency)
  const childUserData = useMemo(() => {
    if (!isChildUser) return { personalNumber: null, unitId: null };

    const personalNumber = user?.employeeId;
    if (!personalNumber) return { personalNumber: null, unitId: null };

    const currentUserPersonnel = personnel.find(
      (p) => p.personalNumber === personalNumber,
    );

    return {
      personalNumber,
      unitId: currentUserPersonnel?.unitId || null,
    };
  }, [isChildUser, user?.employeeId, personnel]);

  const { personalNumber: childUserPersonalNumber, unitId: childUserUnitId } =
    childUserData;

  // Pre-calculate quantities: unit-wide child uses childUserUnit (first+second + חתום ל from second when set)
  const inventoryCalcRole = isChildUser ? "childUserUnit" : userRole;
  const inventoryCalcChildPn = isChildUser ? childUserPersonalNumber : null;
  const enhancedInventory = useInventoryCalculations(
    inventory,
    inventoryCalcRole,
    inventoryCalcChildPn,
  );

  const personnelUnitMap = useMemo(() => {
    const map = new Map();
    if (!personnel || !Array.isArray(personnel)) return map;
    personnel.forEach((emp) => {
      const pn =
        emp.personalNumber != null ? String(emp.personalNumber).trim() : "";
      if (pn && emp.unitId) {
        map.set(pn, emp.unitId);
      }
    });
    return map;
  }, [personnel]);

  const nicknameByPersonalNumber = useMemo(
    () => buildNicknameByPersonalNumberMap(personnel),
    [personnel],
  );

  /** Per-row search strings — built only when inventory/personnel changes, not on each search keystroke */
  const inventoryItemSearchIndex = useMemo(() => {
    const map = new Map();
    if (!enhancedInventory?.length) return map;
    for (const item of enhancedInventory) {
      map.set(item.id, {
        nameLower: normalizeItemSearchText(item.itemName),
        serialLower: normalizeItemSearchText(item.serialNumber),
        assigneeHaystack: buildAssigneeSearchHaystack(
          item.assignedPersonnel || [],
          nicknameByPersonalNumber,
          personnel,
        ),
      });
    }
    return map;
  }, [enhancedInventory, nicknameByPersonalNumber, personnel]);

  const parent9263FirestoreId = useMemo(
    () => getParent9263FirestoreId(units),
    [units],
  );

  // userPermissions already defined above

  const location = useLocation();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const canScan = !isChildUser && !!process.env.REACT_APP_VISION_API_URL;
  const [editingItem, setEditingItem] = useState(null);
  /** Prefill "הוסף למלאי" from Signature shortcut (no id — not an edit row) */
  const [stockFormPrefill, setStockFormPrefill] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tsavSending, setTsavSending] = useState(false);
  const [tsavDailyCount, setTsavDailyCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.ALL);
  const [inventoryUnitFilterIds, setInventoryUnitFilterIds] = useState([]);
  const inventoryUnitFilterSelectedSet = useMemo(
    () => new Set(inventoryUnitFilterIds),
    [inventoryUnitFilterIds],
  );
  const [currentPage, setCurrentPage] = useState(1);
  /** detail = one row per serial (default); grouped = serials collapsed by definition */
  const [inventorySerialViewMode, setInventorySerialViewMode] =
    useState("detail");
  /** Inline מיקום/הערה editing — paginated desktop table only (virtual table keeps its own state). */
  const [paginatedTableNoteEditId, setPaginatedTableNoteEditId] =
    useState(null);
  const [paginatedTableNoteValue, setPaginatedTableNoteValue] = useState("");
  const [paginatedTableNoteMeta, setPaginatedTableNoteMeta] = useState(null);

  // Responsive items per page based on viewport height
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (typeof window !== "undefined") {
      const availableHeight = window.innerHeight - HEADER_AND_CONTROLS_HEIGHT;
      const calculatedItems = Math.max(
        ITEMS_PER_PAGE_BASE,
        Math.floor(availableHeight / ROW_HEIGHT),
      );
      return calculatedItems;
    }
    return ITEMS_PER_PAGE_BASE;
  });

  // Signature shortcut: jump here with serial (and optional item def) to add stock first
  useEffect(() => {
    const shortcut = location.state && location.state.addStockShortcut;
    if (!shortcut || isChildUser) return;

    setEditingItem(null);
    setStockFormPrefill({
      itemDefinitionId: shortcut.itemDefinitionId || "",
      serialNumber: String(shortcut.serialNumber || "").trim(),
      quantity: 1,
      isOrganic:
        typeof shortcut.isOrganic === "boolean" ? shortcut.isOrganic : null,
      currentUnitId: "",
    });
    setShowForm(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, isChildUser, navigate]);

  // Load shared Tsav SMS daily count on mount
  useEffect(() => {
    getTsavDailyUsageShared().then((c) => setTsavDailyCount(c));
  }, []);

  // Update items per page on window resize
  useEffect(() => {
    const handleResize = () => {
      const availableHeight = window.innerHeight - HEADER_AND_CONTROLS_HEIGHT;
      const calculatedItems = Math.max(
        ITEMS_PER_PAGE_BASE,
        Math.floor(availableHeight / ROW_HEIGHT),
      );
      setItemsPerPage(calculatedItems);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnItemData, setReturnItemData] = useState(null);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [distributionModal, setDistributionModal] = useState({
    show: false,
    item: null,
    view: "unit",
  });
  const [showSeenSettingsModal, setShowSeenSettingsModal] = useState(false);
  const [showExcelExportMenu, setShowExcelExportMenu] = useState(false);
  const excelExportMenuRef = useRef(null);
  const [refundModalData, setRefundModalData] = useState(null);
  const [seenSettings, setSeenSettings] = useState(() => {
    // Initialize from localStorage or use defaults
    const savedSettings = localStorage.getItem("seenSettings");
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (e) {
        console.error("Error loading seen settings:", e);
      }
    }
    return { greenMax: 2, yellowDays: 3 };
  });

  // Performance optimization: Debounced search to reduce unnecessary re-renders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Optimized filter and search logic with pagination
  const filteredInventory = useMemo(() => {
    if (!enhancedInventory.length) return [];

    let filtered = enhancedInventory;

    // Child: all rows for their יחידה (fallback: only first[child] if no unit on personnel)
    if (isChildUser && childUserPersonalNumber) {
      filtered = enhancedInventory.filter((item) =>
        itemBelongsToChildUnit(
          item,
          childUserUnitId,
          personnelUnitMap,
          childUserPersonalNumber,
        ),
      );
    } else if (isChildUser && !childUserPersonalNumber) {
      filtered = [];
    } else {
      // For admin and parentsUser: use status filter
      if (statusFilter === STATUS_FILTERS.AVAILABLE) {
        filtered = enhancedInventory.filter(
          (item) => item.calculatedStatus === "available",
        );
      } else if (statusFilter === STATUS_FILTERS.ASSIGNED) {
        filtered = enhancedInventory.filter(
          (item) =>
            item.calculatedStatus === "assigned" ||
            (item.assignedQuantity !== undefined && item.assignedQuantity > 0),
        );
      } else if (statusFilter === STATUS_FILTERS.RETURNED) {
        filtered = enhancedInventory.filter(
          (item) => item.calculatedStatus === "returned",
        );
      } else if (statusFilter === STATUS_FILTERS.NOT_SEEN) {
        filtered = enhancedInventory.filter((item) =>
          itemMatchesNotSeenSerialFilter(item, seenSettings),
        );
      } else if (statusFilter === STATUS_FILTERS.SERIAL_ONLY) {
        filtered = enhancedInventory.filter((item) => {
          return (
            item.serialNumber &&
            item.serialNumber.trim() !== "" &&
            item.calculatedStatus !== "returned"
          );
        });
      } else if (statusFilter === STATUS_FILTERS.ALL) {
        filtered = enhancedInventory.filter(
          (item) => item.calculatedStatus !== "returned",
        );
      }
    }

    // Search filter with debounced term — uses precomputed index (no per-keystroke haystack rebuild)
    if (debouncedSearchTerm) {
      const searchLower = normalizeItemSearchText(debouncedSearchTerm);
      filtered = filtered.filter((item) => {
        const idx = inventoryItemSearchIndex.get(item.id);
        const nameLower =
          idx?.nameLower ?? normalizeItemSearchText(item.itemName);
        const serialLower =
          idx?.serialLower ?? normalizeItemSearchText(item.serialNumber);
        const assigneeHaystack =
          idx?.assigneeHaystack ??
          buildAssigneeSearchHaystack(
            item.assignedPersonnel || [],
            nicknameByPersonalNumber,
            personnel,
          );
        return (
          nameLower.includes(searchLower) ||
          serialLower.includes(searchLower) ||
          assigneeHaystack.includes(searchLower)
        );
      });
    }

    if (inventoryUnitFilterSelectedSet.size > 0) {
      filtered = filtered.filter((item) =>
        itemMatchesUnitFilterSelection(
          item,
          inventoryUnitFilterSelectedSet,
          personnelUnitMap,
          parent9263FirestoreId,
        ),
      );
    }

    // Sort the filtered results - only if needed
    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle different data types efficiently
        if (sortField === "quantity") {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        } else if (sortField === "createdAt" || sortField === "updatedAt") {
          aValue = aValue?.seconds
            ? aValue.seconds * 1000
            : new Date(aValue).getTime();
          bValue = bValue?.seconds
            ? bValue.seconds * 1000
            : new Date(bValue).getTime();
        } else if (sortField === "seenAt") {
          aValue = getSeenAtSortKey(
            a,
            seenSettings.greenMax,
            seenSettings.yellowDays,
          );
          bValue = getSeenAtSortKey(
            b,
            seenSettings.greenMax,
            seenSettings.yellowDays,
          );
        } else if (sortField === "assignedTo") {
          return compareAssignedToSort(
            a,
            b,
            sortDirection,
            nicknameByPersonalNumber,
            personnel,
          );
        } else if (sortField === "currentUnitId") {
          const ua = getInventoryUnitColumnSortKey(
            a,
            personnelUnitMap,
            units,
            userRole,
            childUserUnitId,
          );
          const ub = getInventoryUnitColumnSortKey(
            b,
            personnelUnitMap,
            units,
            userRole,
            childUserUnitId,
          );
          const cmp = ua.localeCompare(ub, "he");
          return sortDirection === "desc" ? -cmp : cmp;
        } else {
          aValue = String(aValue || "").toLowerCase();
          bValue = String(bValue || "").toLowerCase();
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [
    enhancedInventory,
    debouncedSearchTerm,
    statusFilter,
    sortField,
    sortDirection,
    seenSettings,
    inventoryUnitFilterSelectedSet,
    personnelUnitMap,
    parent9263FirestoreId,
    isChildUser,
    childUserPersonalNumber,
    units,
    userRole,
    childUserUnitId,
    nicknameByPersonalNumber,
    personnel,
    inventoryItemSearchIndex,
  ]);

  const filteredInventoryById = useMemo(() => {
    const m = new Map();
    for (const row of filteredInventory) {
      m.set(row.id, row);
    }
    return m;
  }, [filteredInventory]);

  const displayedInventory = useMemo(() => {
    if (inventorySerialViewMode === "grouped") {
      return buildSerialGroupedInventoryRows(filteredInventory);
    }
    return filteredInventory;
  }, [filteredInventory, inventorySerialViewMode]);

  const toggleInventorySerialViewMode = useCallback(() => {
    setInventorySerialViewMode((m) => (m === "grouped" ? "detail" : "grouped"));
    setCurrentPage(1);
  }, []);

  /** סטטיסטיקות כרטיסים: לפי מלאי מלא (יחידות + ילד), בלי סינון סטטוס וחיפוש — כך המספרים לא מתאפסים בזוכו/זמינים וכו׳ */
  const inventoryForStatsCounts = useMemo(() => {
    if (!enhancedInventory.length) return [];

    let base = enhancedInventory;

    if (isChildUser && childUserPersonalNumber) {
      base = enhancedInventory.filter((item) =>
        itemBelongsToChildUnit(
          item,
          childUserUnitId,
          personnelUnitMap,
          childUserPersonalNumber,
        ),
      );
    } else if (isChildUser && !childUserPersonalNumber) {
      return [];
    }

    if (inventoryUnitFilterSelectedSet.size > 0) {
      base = base.filter((item) =>
        itemMatchesUnitFilterSelection(
          item,
          inventoryUnitFilterSelectedSet,
          personnelUnitMap,
          parent9263FirestoreId,
        ),
      );
    }

    return base;
  }, [
    enhancedInventory,
    isChildUser,
    childUserPersonalNumber,
    childUserUnitId,
    personnelUnitMap,
    inventoryUnitFilterSelectedSet,
    parent9263FirestoreId,
  ]);

  // Optimized pagination logic
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(displayedInventory.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedInventory = displayedInventory.slice(startIndex, endIndex);

    return {
      totalPages,
      startIndex,
      endIndex,
      paginatedInventory,
    };
  }, [displayedInventory, currentPage, itemsPerPage]);

  const { totalPages, startIndex, endIndex, paginatedInventory } =
    paginationData;

  // Determine if we should use virtual scrolling (desktop only)
  const shouldUseVirtualScroll = useMemo(() => {
    if (isMobile) return false;
    if (inventorySerialViewMode === "grouped") return true;
    return displayedInventory.length >= VIRTUAL_SCROLL_THRESHOLD;
  }, [
    displayedInventory.length,
    inventorySerialViewMode,
    isMobile,
  ]);

  useEffect(() => {
    if (!shouldUseVirtualScroll || isMobile) return;
    setPaginatedTableNoteEditId(null);
    setPaginatedTableNoteMeta(null);
  }, [shouldUseVirtualScroll, isMobile]);

  // Mobile virtual scroll setup - only for mobile
  const mobileContainerRef = useRef(null);
  const mobileCardHeightRef = useRef(null); // Ref to measure actual card height
  const desktopVirtualScrollContainerRef = useRef(null);
  const virtualScrollApiRef = useRef(null);
  const paginatedTableScrollRef = useRef(null);
  const pendingSeenScrollRestoreRef = useRef(null);

  // Calculate mobile container height based on viewport
  const mobileContainerHeight = useMemo(() => {
    if (!isMobile || typeof window === "undefined") return 500;
    // Reserve space for header, stats, add bar, controls (~456px on mobile)
    const availableHeight = window.innerHeight - 456;
    return Math.max(400, Math.min(availableHeight * 0.9, 1200));
  }, [isMobile]);

  // Dynamic card height calculation - measure first card or use estimate
  const [mobileCardHeight, setMobileCardHeight] = useState(280); // Default estimate
  const MOBILE_CARD_GAP = 16; // Gap between cards from CSS

  // Virtual scroll hook for mobile - only active when isMobile is true
  // Must be defined BEFORE useEffect that uses mobileVisibleItems
  const {
    visibleItems: mobileVisibleItems,
    totalHeight: mobileTotalHeight,
    offsetY: mobileOffsetY,
    setContainerRef: setMobileContainerRef,
    applyScrollTop: mobileApplyScrollTop,
  } = useVirtualScroll(
    isMobile ? displayedInventory : [], // Only pass items if mobile
    mobileCardHeight, // Use dynamic height
    mobileContainerHeight,
  );

  // Measure actual card height after first render (only on mobile)
  useEffect(() => {
    if (!isMobile || !mobileCardHeightRef.current) return;

    const measureCardHeight = () => {
      const cardElement = mobileCardHeightRef.current;
      if (cardElement && cardElement.firstChild) {
        // Get the actual MobileInventoryCard element
        const card = cardElement.firstChild;
        const measuredHeight = card.offsetHeight || cardElement.offsetHeight;
        // Use measured height + gap, with a minimum
        if (measuredHeight > 0) {
          const newHeight = measuredHeight + MOBILE_CARD_GAP;
          setMobileCardHeight(newHeight);
        }
      }
    };

    // Measure after a short delay to ensure card is rendered
    const timeoutId = setTimeout(measureCardHeight, 150);

    // Also measure on window resize
    const resizeHandler = () => {
      setTimeout(measureCardHeight, 100);
    };
    window.addEventListener("resize", resizeHandler, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", resizeHandler);
    };
  }, [isMobile, displayedInventory.length, mobileVisibleItems.length]); // Re-measure when visible items change

  // Use a callback ref to ensure the ref is set immediately when the element mounts
  // This ensures the scroll listener is attached as soon as the container is available
  const setMobileContainerCallback = useCallback(
    (node) => {
      mobileContainerRef.current = node;
      if (node && isMobile) {
        // Set the ref in the virtual scroll hook immediately
        setMobileContainerRef(node);
      } else if (!node || !isMobile) {
        // Clear ref when element unmounts or not mobile
        setMobileContainerRef(null);
      }
    },
    [isMobile, setMobileContainerRef],
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, inventoryUnitFilterIds]);

  useEffect(() => {
    if (!showExcelExportMenu) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowExcelExportMenu(false);
    };
    const onPointerDown = (e) => {
      if (
        excelExportMenuRef.current &&
        !excelExportMenuRef.current.contains(e.target)
      ) {
        setShowExcelExportMenu(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [showExcelExportMenu]);

  // Reset status filter for childUser if set to returned, not_seen, or serial_only (only check when needed)
  useEffect(() => {
    if (
      isChildUser &&
      (statusFilter === STATUS_FILTERS.RETURNED ||
        statusFilter === STATUS_FILTERS.NOT_SEEN ||
        statusFilter === STATUS_FILTERS.SERIAL_ONLY)
    ) {
      setStatusFilter(STATUS_FILTERS.ALL);
    }
  }, [isChildUser, statusFilter]);

  // Handle column sorting (חתום ל: א-ת → זמינים קודם → ת-א → …)
  const handleSort = (field) => {
    if (inventorySerialViewMode === "grouped") return;
    if (field === "assignedTo") {
      if (sortField !== "assignedTo") {
        setSortField("assignedTo");
        setSortDirection("asc");
      } else if (sortDirection === "asc") {
        setSortDirection(ASSIGNED_TO_SORT_AVAIL);
      } else if (sortDirection === ASSIGNED_TO_SORT_AVAIL) {
        setSortDirection("desc");
      } else {
        setSortDirection("asc");
      }
      setCurrentPage(1);
      return;
    }
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const stats = useMemo(() => {
    const inventoryToCalculate = inventoryForStatsCounts;

    if (!inventoryToCalculate.length) {
      return {
        total: 0,
        available: 0,
        assigned: 0,
        returned: 0,
        serialTotal: 0,
        serialSeenIn2Days: 0,
      };
    }

    // Single pass through array for efficiency
    let available = 0;
    let assigned = 0;
    let returned = 0;
    let nonReturnedTotal = 0;
    let serialTotal = 0;
    let serialSeenIn2Days = 0;

    inventoryToCalculate.forEach((item) => {
      const status = item.calculatedStatus;
      if (status === "available") available++;
      else if (status === "assigned") assigned++;
      else if (status === "returned") returned++;

      if (status !== "returned") {
        nonReturnedTotal++;
      }

      // Count serial items and those seen in last 2 days
      const isSerial = item.serialNumber && item.serialNumber.trim() !== "";
      if (isSerial && status !== "returned") {
        serialTotal++;
        const seenStatus = getSeenStatus(
          item.seenAt,
          seenSettings.greenMax,
          seenSettings.yellowDays,
        );
        if (
          seenStatus?.colorClass === "seen-green" ||
          seenStatus?.colorClass === "seen-yellow"
        ) {
          serialSeenIn2Days++;
        }
      }
    });

    return {
      total: nonReturnedTotal,
      available,
      assigned,
      returned,
      serialTotal,
      serialSeenIn2Days,
    };
  }, [inventoryForStatsCounts, seenSettings]);

  const notSeenSerialCount = useMemo(() => {
    let n = 0;
    for (const item of inventoryForStatsCounts) {
      if (itemMatchesNotSeenSerialFilter(item, seenSettings)) n++;
    }
    return n;
  }, [inventoryForStatsCounts, seenSettings]);

  // Handle form submission
  const handleFormSubmit = async (formData) => {
    try {
      // Validation for serial number items
      if (formData.validateSerialNumber && formData.serialNumber) {
        const existingSerialItem = inventory.find(
          (item) => item.serialNumber === formData.serialNumber,
        );
        if (existingSerialItem) {
          alert(`מספר סידורי ${formData.serialNumber} כבר קיים במערכת`);
          return;
        }
      }

      // Validation for non-serial items - check for existing item to merge (only for new items)
      if (
        formData.validateQuantityMerge &&
        !formData.serialNumber &&
        !formData.isEdit
      ) {
        const existingItem = inventory.find(
          (item) =>
            item.itemDefinitionId === formData.itemDefinitionId &&
            item.isOrganic === formData.isOrganic &&
            !item.serialNumber &&
            item.status === "available",
        );

        if (existingItem) {
          const confirmMerge = window.confirm(
            `פריט זה כבר קיים במערכת עם כמות ${existingItem.quantity}. האם ברצונך להוסיף ${formData.quantity} לכמות הקיימת?`,
          );

          if (confirmMerge) {
            const updatedQuantity = existingItem.quantity + formData.quantity;
            const oldAvailable =
              existingItem.availableQuantity ?? existingItem.quantity;
            const newAvailable = oldAvailable + formData.quantity;
            const result = await updateInventoryItem(existingItem.id, {
              ...existingItem,
              quantity: updatedQuantity,
              availableQuantity: newAvailable,
            });

            if (result.success) {
              setShowForm(false);
              setEditingItem(null);
              alert(`הפריט עודכן בהצלחה. כמות חדשה: ${updatedQuantity}`);
            } else {
              alert(`שגיאה בעדכון הפריט: ${result.error}`);
            }
            return;
          } else {
            return; // User cancelled
          }
        }
      }

      // Recalculate availableQuantity when editing a non-serial item's quantity
      if (editingItem && !formData.serialNumber) {
        const oldQty = editingItem.quantity || 0;
        const oldAvailable = editingItem.availableQuantity ?? oldQty;
        const assignedQty = oldQty - oldAvailable;
        if (formData.quantity !== oldQty) {
          formData.availableQuantity = Math.max(
            0,
            formData.quantity - assignedQty,
          );
        }
      }

      const result = editingItem
        ? await updateInventoryItem(editingItem.id, formData)
        : await addInventoryItem(formData);

      if (result.success) {
        setShowForm(false);
        setEditingItem(null);
        setStockFormPrefill(null);
      } else {
        alert(`שגיאה: ${result.error}`);
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      alert(`שגיאה: ${error.message}`);
    }
  };

  // Memoized action handlers for better performance
  const handleEdit = useCallback((item) => {
    setStockFormPrefill(null);
    setEditingItem(item);
    setShowForm(true);
  }, []);

  const handleReturn = useCallback(
    async (itemId, quantity = null) => {
      const item = inventory.find((item) => item.id === itemId);
      if (!item) return;

      // Check if serial item is assigned - can't return assigned items
      if (
        item.serialNumber &&
        (item.status === "assigned" || item.calculatedStatus === "assigned")
      ) {
        alert(
          "לא ניתן להחזיר פריט שמוחתם. יש להחזיר אותו דרך מסך החתימה/זיכוי תחילה.",
        );
        return;
      }

      // For non-serial items without quantity specified, show modal
      if (!item.serialNumber && quantity === null) {
        const availableQty = item.availableQuantity ?? item.quantity ?? 0;
        if (availableQty <= 0) {
          alert("אין כמות זמינה להחזרה. הפריט מוחתם במלואו.");
          return;
        }
        // Show modal for quantity selection
        setReturnItemData(item);
        setReturnQuantity(1);
        setShowReturnModal(true);
        return;
      }

      // For serial items or non-serial items with quantity specified, proceed with return
      let confirmMessage = "האם אתה בטוח שברצונך לזכות לחטיבה פריט זה?";

      if (quantity && quantity > 1) {
        confirmMessage = `האם אתה בטוח שברצונך לזכות ${quantity} יחידות מהפריט "${item.itemName}"?`;
      }

      if (window.confirm(confirmMessage)) {
        setOperationLoading(true);
        setOperationError(null);

        try {
          const result = await returnItem(itemId, quantity);
          if (!result.success) {
            setOperationError(`שגיאה בהחזרה: ${result.error}`);
          }
        } catch (error) {
          setOperationError(`שגיאה בהחזרה: ${error.message}`);
        } finally {
          setOperationLoading(false);
        }
      }
    },
    [returnItem, inventory],
  );

  const handleReturnConfirm = useCallback(async () => {
    if (!returnItemData) return;

    const availableQty =
      returnItemData.availableQuantity ?? returnItemData.quantity ?? 0;
    if (returnQuantity <= 0 || returnQuantity > availableQty) {
      alert(`כמות לא תקינה. ניתן להחזיר עד ${availableQty} יחידות.`);
      return;
    }

    if (
      window.confirm(
        `האם אתה בטוח שברצונך לזכות ${returnQuantity} יחידות מהפריט "${returnItemData.itemName}"?`,
      )
    ) {
      setOperationLoading(true);
      setOperationError(null);

      try {
        const result = await returnItem(returnItemData.id, returnQuantity);
        if (!result.success) {
          setOperationError(`שגיאה בהחזרה: ${result.error}`);
        } else {
          setShowReturnModal(false);
          setReturnItemData(null);
          setReturnQuantity(1);
        }
      } catch (error) {
        setOperationError(`שגיאה בהחזרה: ${error.message}`);
      } finally {
        setOperationLoading(false);
      }
    }
  }, [returnItemData, returnQuantity, returnItem]);

  const handleReturnCancel = useCallback(() => {
    setShowReturnModal(false);
    setReturnItemData(null);
    setReturnQuantity(1);
  }, []);

  const handleMakeAvailable = useCallback(
    async (itemId) => {
      if (window.confirm("האם אתה בטוח שברצונך להחזיר פריט זה לזמין?")) {
        setOperationLoading(true);
        setOperationError(null);

        try {
          const result = await makeItemAvailable(itemId);
          if (!result.success) {
            setOperationError(`שגיאה בהחזרה לזמין: ${result.error}`);
          }
        } catch (error) {
          setOperationError(`שגיאה בהחזרה לזמין: ${error.message}`);
        } finally {
          setOperationLoading(false);
        }
      }
    },
    [makeItemAvailable],
  );

  const handleDelete = useCallback(
    async (itemId) => {
      if (
        window.confirm(
          "האם אתה בטוח שברצונך למחוק פריט זה? פעולה זו לא ניתנת לביטול.",
        )
      ) {
        setOperationLoading(true);
        setOperationError(null);

        try {
          const result = await deleteInventoryItem(itemId);
          if (!result.success) {
            setOperationError(`שגיאה במחיקה: ${result.error}`);
          }
        } catch (error) {
          setOperationError(`שגיאה במחיקה: ${error.message}`);
        } finally {
          setOperationLoading(false);
        }
      }
    },
    [deleteInventoryItem],
  );

  const captureListScrollForSeen = useCallback(() => {
    const windowY =
      typeof window !== "undefined" ? window.scrollY : null;
    let list = null;
    if (isMobile && mobileContainerRef.current) {
      list = mobileContainerRef.current.scrollTop;
    } else if (
      !isMobile &&
      shouldUseVirtualScroll &&
      desktopVirtualScrollContainerRef.current
    ) {
      list = desktopVirtualScrollContainerRef.current.scrollTop;
    } else if (
      !isMobile &&
      !shouldUseVirtualScroll &&
      paginatedTableScrollRef.current
    ) {
      list = paginatedTableScrollRef.current.scrollTop;
    }
    return { list, windowY };
  }, [isMobile, shouldUseVirtualScroll]);

  const handleConfirmSeen = useCallback(
    async (itemId, seenAtFromRow) => {
      pendingSeenScrollRestoreRef.current = captureListScrollForSeen();
      try {
        const result = await updateSeenAt(itemId, {
          ...seenSettings,
          seenAt: seenAtFromRow,
        });
        if (!result.success) {
          pendingSeenScrollRestoreRef.current = null;
          setOperationError(`שגיאה בעדכון: ${result.error}`);
        }
      } catch (error) {
        pendingSeenScrollRestoreRef.current = null;
        setOperationError(`שגיאה בעדכון: ${error.message}`);
      }
    },
    [updateSeenAt, seenSettings, captureListScrollForSeen],
  );

  useLayoutEffect(() => {
    const snap = pendingSeenScrollRestoreRef.current;
    if (snap == null) return;
    pendingSeenScrollRestoreRef.current = null;
    const { list, windowY } = snap;
    if (list != null && Number.isFinite(list)) {
      if (isMobile) {
        mobileApplyScrollTop(list);
      } else if (shouldUseVirtualScroll) {
        virtualScrollApiRef.current?.applyScrollTop(list);
      } else if (paginatedTableScrollRef.current) {
        paginatedTableScrollRef.current.scrollTop = list;
      }
    }
    if (
      windowY != null &&
      Number.isFinite(windowY) &&
      typeof window !== "undefined"
    ) {
      window.scrollTo(0, windowY);
    }
  }, [
    inventory,
    isMobile,
    shouldUseVirtualScroll,
    mobileApplyScrollTop,
  ]);

  const handleUpdateNote = useCallback(
    async (itemId, personalNumber, level, newNote) => {
      if (!personalNumber) return;
      await updateAssignmentNote(itemId, personalNumber, level, newNote);
    },
    [updateAssignmentNote],
  );

  // Optimized helper functions with better memoization
  const getItemDefinitionName = useCallback(
    (itemDefinitionId) => {
      if (!itemDefinitionId || !itemsSettings.length) return "לא זמין";
      const itemDef = itemsSettings.find(
        (item) => item.id === itemDefinitionId,
      );
      return itemDef ? itemDef.itemName : "לא זמין";
    },
    [itemsSettings],
  );

  const getUnitName = useCallback(
    (unitId) => {
      if (!unitId || !units.length) return "";
      const unit = units.find((u) => u.id === unitId);
      return unit ? unit.unitName : "";
    },
    [units],
  );

  const personnelNameMap = useMemo(() => {
    const map = new Map();
    if (!personnel || !Array.isArray(personnel)) return map;
    personnel.forEach((p) => {
      const name = formatPersonDisplayName(p);
      const pnKey =
        p.personalNumber != null ? String(p.personalNumber).trim() : "";
      if (pnKey) map.set(pnKey, name);
      const emailKey = (p.email || "").trim().toLowerCase();
      if (emailKey) map.set(emailKey, name);
    });
    // Map app users (admin/parentsUser) by email → their personnel name
    if (Array.isArray(appUsers)) {
      appUsers.forEach((u) => {
        if (u.email && u.employeeId) {
          const p = findPersonnelById(personnel, u.employeeId);
          if (p) {
            map.set(String(u.email).trim().toLowerCase(), formatPersonDisplayName(p));
          }
        }
      });
    }
    return map;
  }, [personnel, appUsers]);

  const resolveAssignedByName = useCallback(
    (assignedById) => {
      if (!assignedById) return "-";
      const raw = String(assignedById).trim();
      return (
        personnelNameMap.get(raw) ||
        personnelNameMap.get(raw.toLowerCase()) ||
        assignedById
      );
    },
    [personnelNameMap],
  );

  const handleShowDistribution = useCallback(
    (item) => {
      let modalItem = item;
      if (
        item.isSerialGroupRow === true &&
        Array.isArray(item.serialGroupMemberIds) &&
        item.serialGroupMemberIds.length > 0
      ) {
        const members = [];
        for (const id of item.serialGroupMemberIds) {
          const row = filteredInventoryById.get(id);
          if (row) members.push(row);
        }
        modalItem = {
          ...item,
          assignments: {
            ...(item.assignments || {}),
            first: mergeAssignmentsFirstFromMembers(members),
          },
        };
      } else if (
        item.serialNumber &&
        String(item.serialNumber).trim() !== "" &&
        item.isSerialGroupRow !== true
      ) {
        const groupKey = getSerialInventoryGroupKey(item);
        const members = [];
        for (const row of filteredInventory) {
          if (
            row.serialNumber &&
            String(row.serialNumber).trim() !== "" &&
            getSerialInventoryGroupKey(row) === groupKey
          ) {
            members.push(row);
          }
        }
        if (members.length > 0) {
          const quantity = members.reduce(
            (s, m) => s + (Number(m.displayQuantity ?? m.quantity) || 0),
            0,
          );
          const availableQuantity = members.reduce(
            (s, m) => s + (Number(m.availableQuantity) || 0),
            0,
          );
          const assignedQuantity = members.reduce(
            (s, m) => s + (Number(m.assignedQuantity) || 0),
            0,
          );
          modalItem = {
            ...item,
            quantity,
            displayQuantity: quantity,
            availableQuantity,
            assignedQuantity,
            assignments: {
              ...(item.assignments || {}),
              first: mergeAssignmentsFirstFromMembers(members),
            },
            distributionAggregatesMultipleSerials: members.length > 1,
            distributionSerialMemberIds: members.map((m) => m.id),
          };
        }
      }
      setDistributionModal({ show: true, item: modalItem, view: "customer" });
    },
    [filteredInventoryById, filteredInventory],
  );

  const handleCloseDistribution = useCallback(() => {
    setDistributionModal({ show: false, item: null, view: "unit" });
  }, []);

  const handleAssignedNameClick = useCallback(
    (item) => {
      const assignedPersonnel = item.assignedPersonnel || [];
      if (assignedPersonnel.length === 0) return;

      const firstAssigned = assignedPersonnel[0];
      const employeeRecord = personnel.find(
        (p) => p.personalNumber === firstAssigned.personalNumber,
      );

      if (!employeeRecord) return;

      setRefundModalData({ item, employee: employeeRecord });
    },
    [personnel],
  );

  const handleDistributionNameClick = useCallback(
    (entry) => {
      const employeeRecord = personnel.find(
        (p) => p.personalNumber === entry.personalNumber,
      );
      if (!employeeRecord) return;

      const item = distributionModal.item;
      handleCloseDistribution();
      setRefundModalData({ item, employee: employeeRecord });
    },
    [personnel, distributionModal.item, handleCloseDistribution],
  );

  const distributionData = useMemo(() => {
    if (!distributionModal.show || !distributionModal.item) return null;
    const item = distributionModal.item;

    const serialMemberIds =
      item.distributionSerialMemberIds || item.serialGroupMemberIds;
    const distributionMemberRows =
      Array.isArray(serialMemberIds) && serialMemberIds.length > 0
        ? serialMemberIds
            .map((id) => filteredInventoryById.get(id))
            .filter(Boolean)
        : item.serialNumber &&
            String(item.serialNumber).trim() !== "" &&
            item.isSerialGroupRow !== true
          ? [item]
          : [];
    const showSerialColumnCustomer =
      distributionMemberRows.length > 0 &&
      distributionMemberRows.some(
        (m) => m.serialNumber && String(m.serialNumber).trim() !== "",
      );

    const assignments = item.assignments?.first || {};

    const byCustomer = Object.entries(assignments)
      .map(([pn, assignment]) => {
        const unitId = getUnitIdForAssigneeKey(personnelUnitMap, pn);
        return {
          personalNumber: pn,
          name:
            formatPersonDisplayName({
              firstName: assignment.firstName,
              lastName: assignment.lastName,
              nickname: nicknameByPersonalNumber.get(pn) || "",
            }).trim() ||
            personnelNameMap.get(pn) ||
            pn,
          quantity: assignment.quantity || 0,
          unitName: unitId ? getUnitName(unitId) : "לא משויך",
          _unitId: unitId,
          serialsLabel: showSerialColumnCustomer
            ? formatDistributionSerialsForAssignee(distributionMemberRows, pn)
            : "",
        };
      })
      .sort((a, b) => a.unitName.localeCompare(b.unitName, "he"));

    const unitGroups = {};
    byCustomer.forEach((entry) => {
      if (!unitGroups[entry.unitName]) unitGroups[entry.unitName] = 0;
      unitGroups[entry.unitName] += entry.quantity;
    });
    const byUnit = Object.entries(unitGroups).map(([name, quantity]) => ({
      name,
      quantity,
    }));

    const totalAssigned = byCustomer.reduce((sum, c) => sum + c.quantity, 0);

    const totalQuantity = item.quantity ?? 0;
    const totalAvailable = item.availableQuantity ?? 0;

    return {
      byCustomer,
      byUnit,
      totalAssigned,
      totalAvailable,
      totalQuantity,
      showSerialColumnCustomer,
    };
  }, [
    distributionModal,
    personnelUnitMap,
    getUnitName,
    personnelNameMap,
    nicknameByPersonalNumber,
    filteredInventoryById,
  ]);

  const suppressDistributionCustomerNameClick =
    distributionModal.item?.isSerialGroupRow === true ||
    distributionModal.item?.distributionAggregatesMultipleSerials === true;

  const handleExportToExcel = useCallback(() => {
    if (!filteredInventory.length) return;

    const rows = filteredInventory.map((item) => {
      const isSerial = item.serialNumber && item.serialNumber.trim() !== "";
      const assignedTo = isSerial
        ? formatAssignedPersonnelList(
            item.assignedPersonnel || [],
            nicknameByPersonalNumber,
            personnel,
          ) || "-"
        : "";
      const assignedBy =
        item.assignedPersonnel?.length > 0 &&
        (item.calculatedStatus === "assigned" || item.status === "assigned")
          ? resolveAssignedByName(item.assignedPersonnel[0].assignedBy)
          : "-";

      let unitName = "";
      if (
        item.assignedPersonnel?.length > 0 &&
        item.calculatedStatus === "assigned"
      ) {
        const unitId = getUnitIdForAssigneeKey(
          personnelUnitMap,
          item.assignedPersonnel[0].personalNumber,
        );
        unitName = unitId ? getUnitName(unitId) : "";
      } else if (item.currentUnitId) {
        unitName = getUnitName(item.currentUnitId);
      }

      return {
        "שם פריט": item.itemName,
        "מספר סידורי": item.serialNumber || "",
        סוג: item.isOrganic ? "אורגני" : "קווי",
        "כמות כללית": item.displayQuantity ?? item.quantity,
        "כמות מוקצית": item.assignedQuantity ?? 0,
        "כמות זמינה": item.availableQuantity ?? 0,
        סטטוס:
          item.calculatedStatus === "available"
            ? "זמין"
            : item.calculatedStatus === "assigned"
              ? "מוחתם"
              : "זוכה",
        "חתום ל": assignedTo,
        "הוקצה על ידי": assignedBy,
        יחידה: unitName,
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "פריטים");
    const date = new Date().toLocaleDateString("he-IL").replace(/\./g, "-");
    XLSX.writeFile(wb, `פריטים_${date}.xlsx`);
  }, [
    filteredInventory,
    resolveAssignedByName,
    personnelUnitMap,
    getUnitName,
    nicknameByPersonalNumber,
    personnel,
  ]);

  const handleExportTsavToExcel = useCallback(() => {
    const tsavItems = enhancedInventory.filter(
      (item) =>
        item.serialNumber &&
        item.serialNumber.trim() !== "" &&
        item.calculatedStatus !== "returned",
    );
    if (!tsavItems.length) return;

    const rows = tsavItems.map((item) => {
      const seenStatus = getSeenStatus(
        item.seenAt,
        seenSettings.greenMax,
        seenSettings.yellowDays,
      );

      const assignedTo =
        formatAssignedPersonnelList(
          item.assignedPersonnel || [],
          nicknameByPersonalNumber,
          personnel,
        ) || "-";

      let unitName = "";
      if (item.assignedPersonnel?.length > 0) {
        const unitId = getUnitIdForAssigneeKey(
          personnelUnitMap,
          item.assignedPersonnel[0].personalNumber,
        );
        unitName = unitId ? getUnitName(unitId) : "";
      } else if (item.currentUnitId) {
        unitName = getUnitName(item.currentUnitId);
      }

      let seenDateStr = "";
      if (item.seenAt) {
        if (item.seenAt.seconds) {
          seenDateStr = new Date(item.seenAt.seconds * 1000).toLocaleString(
            "he-IL",
          );
        } else {
          try {
            seenDateStr = new Date(item.seenAt).toLocaleString("he-IL");
          } catch {
            seenDateStr = "";
          }
        }
      }

      let seenLabel = "לא ידוע";
      if (seenStatus?.colorClass === "seen-green") seenLabel = "תקין";
      else if (seenStatus?.colorClass === "seen-yellow") seenLabel = "אזהרה";
      else if (seenStatus?.colorClass === "seen-red") seenLabel = "חריגה";

      return {
        "שם פריט": item.itemName,
        "מספר סידורי": item.serialNumber,
        סוג: item.isOrganic ? "אורגני" : "קווי",
        סטטוס:
          item.calculatedStatus === "available"
            ? "זמין"
            : item.calculatedStatus === "assigned"
              ? "מוחתם"
              : "זוכה",
        "חתום ל": assignedTo,
        יחידה: unitName,
        "נראה לאחרונה": seenDateStr,
        "ימים מאז": seenStatus?.days ?? "לא ידוע",
        "סטטוס נראה": seenLabel,
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "דוח צ");
    const date = new Date().toLocaleDateString("he-IL").replace(/\./g, "-");
    XLSX.writeFile(wb, `דוח_צ_${date}.xlsx`);
  }, [
    enhancedInventory,
    seenSettings,
    personnelUnitMap,
    getUnitName,
    nicknameByPersonalNumber,
    personnel,
  ]);

  // Optimized table row component with reduced re-renders
  const InventoryRow = useCallback(
    ({
      item,
      hideGroupedColumns = false,
      userPermissions,
      getUnitName,
      personnelUnitMap,
      handleEdit,
      handleReturn,
      handleMakeAvailable,
      handleDelete,
      handleConfirmSeen,
      seenSettings,
      userRole,
      childUserUnitId,
      childUserPersonalNumber = null,
      resolveAssignedByName,
      onItemNameClick,
      onAssignedNameClick,
      onUpdateNote,
      editingNoteId,
      noteValue,
      setNoteValue,
      editingNoteMeta,
      setEditingNoteId,
      setEditingNoteMeta,
      nicknameByPersonalNumber,
      personnel,
    }) => {
      // Use pre-calculated values (no calculations in render)
      const {
        assignedQuantity,
        availableQuantity,
        calculatedStatus,
        assignedPersonnel,
      } = item;

      // Get assigned personnel names
      // For non-serial items, show empty since they can be assigned to multiple employees
      const isGroup = item.isSerialGroupRow === true;
      const isSerial =
        !isGroup && item.serialNumber && item.serialNumber.trim() !== "";
      const assignedTo = isSerial
        ? assignedPersonnel.length > 0
          ? formatAssignedPersonnelList(
              assignedPersonnel,
              nicknameByPersonalNumber,
              personnel,
            )
          : "-"
        : ""; // Empty for non-serial items

      // Optimized unit name calculation
      let unitName = "";
      if (isChildUser && childUserUnitId && isSerial) {
        // For childUser serial items: always show childUser's unit
        unitName = getUnitName(childUserUnitId);
      } else if (
        assignedPersonnel.length > 0 &&
        calculatedStatus === "assigned"
      ) {
        // Get unit from first assigned employee
        const firstEmployeePersonalNumber = assignedPersonnel[0].personalNumber;
        const employeeUnitId = getUnitIdForAssigneeKey(
          personnelUnitMap,
          firstEmployeePersonalNumber,
        );
        unitName = employeeUnitId ? getUnitName(employeeUnitId) : "";
      } else if (item.currentUnitId) {
        // For non-assigned items, use item's currentUnitId
        unitName = getUnitName(item.currentUnitId);
      }

      const seenStatus = isSerial
        ? getSeenStatus(
            item.seenAt,
            seenSettings.greenMax,
            seenSettings.yellowDays,
          )
        : null;
      const seenBtnClears =
        isSerial &&
        shouldClearSeenOnEyeClick(
          item.seenAt,
          seenSettings.greenMax,
          seenSettings.yellowDays,
        );

      return (
        <tr
          key={item.id}
          className={`inventory-row ${calculatedStatus}${
            isGroup ? " inventory-row-serial-group" : ""
          }`}
        >
          <td data-label="שם פריט">
            <ClickableInventoryItemName
              item={item}
              onItemNameClick={onItemNameClick}
            />
          </td>
          <td
            data-label={
              hideGroupedColumns ? "סטטוס" : "מספר סידורי"
            }
          >
            {isGroup && item.serialGroupSerialCount != null ? (
              <div className="serial-group-serial-cell">
                <div>{`${item.serialGroupSerialCount} סידוריים`}</div>
                {item.serialGroupStatusSummary ? (
                  <div className="serial-group-status-summary">
                    {item.serialGroupStatusSummary}
                  </div>
                ) : null}
              </div>
            ) : (
              item.serialNumber || ""
            )}
          </td>
          <td data-label="סוג">
            <span
              className={`item-type ${
                item.isOrganic ? "organic" : "synthetic"
              }`}
            >
              {item.isOrganic ? "אורגני" : "קווי"}
            </span>
          </td>
          <td data-label="כמות כללית">
            {item.displayQuantity ?? item.quantity}
          </td>
          <td data-label="כמות מוקצית">{assignedQuantity}</td>
          <td data-label="כמות זמינה">{availableQuantity}</td>
          {!hideGroupedColumns && (
            <>
              <td data-label="סטטוס">
                <span className={`status-badge ${calculatedStatus}`}>
                  {calculatedStatus === "available"
                    ? "זמין"
                    : calculatedStatus === "assigned"
                      ? "מוחתם"
                      : "זוכה"}
                </span>
              </td>
              <td data-label="חתום ל">
                {isSerial &&
                calculatedStatus === "assigned" &&
                assignedPersonnel.length > 0 ? (
                  <span
                    className="clickable-assigned-name"
                    onClick={() => onAssignedNameClick?.(item)}
                  >
                    {assignedTo}
                  </span>
                ) : (
                  assignedTo
                )}
              </td>
              {!isChildUser && (
                <td data-label="הוקצה על ידי">
                  {assignedPersonnel.length > 0 &&
                  calculatedStatus === "assigned"
                    ? resolveAssignedByName(
                        assignedPersonnel[0].assignedBy,
                      )
                    : "-"}
                </td>
              )}
              <td data-label="יחידה">{unitName}</td>
              <td data-label="מיקום/הערה" className="note-column-cell">
                {isGroup ? (
                  <span className="note-cell-empty">—</span>
                ) : (
                  (() => {
                    let note = assignedPersonnel?.[0]?.note || "";
                    let isChildNote = false;
                    if (userRole !== "childUser" && !note) {
                      if (isSerial) {
                        const secondAssignments = item.assignments?.second || {};
                        const firstChildEntry =
                          Object.values(secondAssignments)[0];
                        if (firstChildEntry?.note) {
                          note = firstChildEntry.note;
                          isChildNote = true;
                        }
                      }
                    }
                    const isUnitPeerNoteReadonly =
                      userRole === "childUser" &&
                      childUserPersonalNumber &&
                      !childHasFirstAllocation(item, childUserPersonalNumber);
                    const noteReadonly = isChildNote || isUnitPeerNoteReadonly;
                    const isEditing =
                      editingNoteId === item.id && !noteReadonly;
                    if (isEditing) {
                      return (
                        <textarea
                          className="note-cell-textarea"
                          autoFocus
                          value={noteValue}
                          onFocus={(e) => {
                            const len = e.target.value.length;
                            e.target.setSelectionRange(len, len);
                          }}
                          onChange={(e) => setNoteValue(e.target.value)}
                          onBlur={() => {
                            if (
                              onUpdateNote &&
                              editingNoteMeta?.personalNumber &&
                              noteValue !== editingNoteMeta.originalNote
                            ) {
                              onUpdateNote(
                                editingNoteMeta.itemId,
                                editingNoteMeta.personalNumber,
                                editingNoteMeta.level,
                                noteValue,
                              );
                            }
                            setEditingNoteId(null);
                            setEditingNoteMeta(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setEditingNoteId(null);
                              setEditingNoteMeta(null);
                            }
                          }}
                        />
                      );
                    }
                    return (
                      <div
                        className={`note-cell-text${noteReadonly ? " note-child-readonly" : ""}${!note && !noteReadonly ? " note-cell-empty" : ""}`}
                        title={
                          isChildNote
                            ? note || "הערת חייל (לקריאה בלבד)"
                            : isUnitPeerNoteReadonly
                              ? note || "לקריאה בלבד"
                              : note || "לחץ לעריכה"
                        }
                        onClick={() => {
                          if (!noteReadonly) {
                            const level =
                              userRole === "childUser" ? "second" : "first";
                            let personalNumber =
                              assignedPersonnel?.[0]?.personalNumber;
                            if (!personalNumber) {
                              const raw = item.assignments?.[level] || {};
                              personalNumber = Object.keys(raw)[0];
                            }
                            setEditingNoteId(item.id);
                            setNoteValue(note);
                            setEditingNoteMeta({
                              itemId: item.id,
                              personalNumber,
                              level,
                              originalNote: note,
                            });
                          }
                        }}
                      >
                        {note || <span className="note-placeholder">+</span>}
                      </div>
                    );
                  })()
                )}
              </td>
              <td data-label="דוח צ'">
                {isSerial ? (
                  <div
                    className={`seen-cell ${
                      seenStatus?.colorClass || "seen-no-data"
                    }`}
                  >
                    <div className="seen-days">
                      {seenStatus?.days != null ? seenStatus.days : "-"}
                    </div>
                  </div>
                ) : null}
              </td>
              <td data-label="פעולות">
                <div className="action-buttons">
                  {!isGroup && isSerial && (
                    <button
                      onClick={() => handleConfirmSeen(item.id, item.seenAt)}
                      className="seen-btn"
                      title={
                        seenBtnClears
                          ? "בטל סימון נראה (בטעות)"
                          : "אשר נראה"
                      }
                    >
                      <FaEye />
                    </button>
                  )}
                  {!isGroup && !isChildUser && (
                    <button
                      onClick={() => handleEdit(item)}
                      className="edit-btn"
                      title="ערוך"
                    >
                      <FaEdit />
                    </button>
                  )}
                  {!isGroup &&
                    !isChildUser &&
                    (item.status === "returned" ? (
                      <button
                        onClick={() => handleMakeAvailable(item.id)}
                        className="available-btn"
                        title="החזר לזמין"
                      >
                        <FaArrowRight />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReturn(item.id)}
                        className="return-btn"
                        disabled={
                          item.serialNumber &&
                          (calculatedStatus === "assigned" ||
                            item.status === "assigned")
                        }
                        title={
                          item.serialNumber &&
                          (calculatedStatus === "assigned" ||
                            item.status === "assigned")
                            ? "לא ניתן לזכות פריט שחתום"
                            : item.serialNumber
                              ? "זיכוי"
                              : "זיכוי (בחר כמות)"
                        }
                      >
                        <FaArrowLeft />
                      </button>
                    ))}
                  {!isGroup && userPermissions === "admin" && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="delete-btn"
                      title="מחק"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </td>
            </>
          )}
        </tr>
      );
    },
    [
      getUnitName,
      personnelUnitMap,
      handleEdit,
      handleReturn,
      handleMakeAvailable,
      handleDelete,
      handleConfirmSeen,
      seenSettings,
      userPermissions,
      isChildUser,
      childUserUnitId,
      resolveAssignedByName,
      handleShowDistribution,
      nicknameByPersonalNumber,
      personnel,
    ],
  );

  const handleSendTsavReport = async () => {
    const currentCount = await getTsavDailyUsageShared();
    if (currentCount >= 2) {
      alert("הגעת למגבלת 2 שליחות ביום לכלל המשתמשים. ניתן לשלוח שוב מחר.");
      setTsavDailyCount(currentCount);
      return;
    }

    if (
      !window.confirm(
        "פעולה זו תשלח SMS לכל החיילים עם פריטי צ' ותבקש מהם לדווח דוח צ'. האם להמשיך?",
      )
    ) {
      return;
    }

    // Collect unique personal numbers from serial items
    const assignedPersonalNumbers = new Set();
    enhancedInventory.forEach((item) => {
      if (item.serialNumber && item.serialNumber.trim() !== "") {
        Object.keys(item.assignments?.first || {}).forEach((pn) =>
          assignedPersonalNumbers.add(pn),
        );
        Object.keys(item.assignments?.second || {}).forEach((pn) =>
          assignedPersonalNumbers.add(pn),
        );
      }
    });

    const targets = [...assignedPersonalNumbers]
      .map((pn) => personnel.find((p) => p.personalNumber === pn))
      .filter((p) => p && p.phone);

    if (targets.length === 0) {
      alert("לא נמצאו חיילים עם פריטי צ' ומספר טלפון.");
      return;
    }

    setTsavSending(true);
    const senderEmail = user?.email || "";
    for (const p of targets) {
      try {
        const pageId = await getOrCreatePageId(
          p.id,
          p.personalNumber,
          senderEmail,
        );
        const url = getAssignmentUrl(pageId);
        await sendTsavReportSms(
          p.phone,
          `${p.firstName} ${p.lastName}`,
          url,
        );
      } catch (err) {
        console.warn("[Tsav SMS]", p.personalNumber, err);
      }
    }
    await incrementTsavDailyUsageShared();
    setTsavDailyCount((c) => c + 1);
    setTsavSending(false);
    alert(`הודעות דוח צ' נשלחו ל-${targets.length} חיילים.`);
  };

  if (loading) {
    return (
      <div id="inventory-page-scope">
        <div className="inventory-container">
          <div className="loading">טוען מלאי...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div id="inventory-page-scope">
        <div className="inventory-container">
          <div className="error">שגיאה בטעינת המלאי: {error}</div>
        </div>
      </div>
    );
  }

  // Show operation error if exists
  if (operationError) {
    return (
      <div id="inventory-page-scope">
        <div className="inventory-container">
          <div className="error-container">
            <div className="error-message">{operationError}</div>
            <button
              className="retry-btn"
              onClick={() => setOperationError(null)}
            >
              סגור
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="inventory-page-scope"
      className={
        inventorySerialViewMode === "grouped"
          ? "inventory-page--serial-grouped"
          : undefined
      }
    >
      <div className="inventory-container">
        <div className="inventory-header">
          <div className="inventory-header-title-row">
            <h1>הפריטים שלי</h1>
            {!isChildUser && (
              <div className="header-action-icons">
                <div className="excel-export-wrap" ref={excelExportMenuRef}>
                  <button
                    type="button"
                    className="excel-header-icon-btn"
                    onClick={() => setShowExcelExportMenu((open) => !open)}
                    title="ייצוא לאקסל"
                    aria-expanded={showExcelExportMenu}
                    aria-haspopup="true"
                  >
                    <FaFileExcel aria-hidden />
                  </button>
                  {showExcelExportMenu && (
                    <>
                      <div
                        className="excel-export-backdrop"
                        aria-hidden
                        onClick={() => setShowExcelExportMenu(false)}
                      />
                      <div
                        className="excel-export-popover"
                        role="dialog"
                        aria-label="בחירת ייצוא לאקסל"
                      >
                        <div className="excel-export-popover-title">
                          בחר ייצוא
                        </div>
                        <button
                          type="button"
                          className="excel-export-option"
                          disabled={!filteredInventory.length}
                          onClick={() => {
                            handleExportToExcel();
                            setShowExcelExportMenu(false);
                          }}
                        >
                          ייצא רשימה נוכחית לאקסל
                        </button>
                        <button
                          type="button"
                          className="excel-export-option excel-export-option-tsav"
                          onClick={() => {
                            handleExportTsavToExcel();
                            setShowExcelExportMenu(false);
                          }}
                        >
                          ייצא דוח צ לאקסל
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className="sms-header-icon-btn"
                  onClick={handleSendTsavReport}
                  disabled={tsavSending || tsavDailyCount >= 2}
                  title="שלח SMS לכל בעלי פריטי צ' ובקש לדווח דוח צ"
                >
                  <FaSms aria-hidden />
                </button>
              </div>
            )}
          </div>
          {!isChildUser && (
            <div className="inventory-header-add-scan-row">
              {canScan && (
                <button
                  className="inventory-scan-icon-btn"
                  onClick={() => setShowScanModal(true)}
                  disabled={operationLoading}
                  title="סרוק תמונה / קבלה"
                >
                  <FaCamera />
                </button>
              )}
              <button
                className="add-item-btn"
                onClick={() => {
                  setEditingItem(null);
                  setStockFormPrefill(null);
                  setShowForm(true);
                }}
                disabled={operationLoading}
              >
                {operationLoading ? "מעבד..." : "הוסף פריט"}
              </button>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="inventory-stats">
          <div
            className={`stat-card clickable-stat${
              statusFilter === STATUS_FILTERS.ALL ? " stat-card--active" : ""
            }`}
            onClick={() => setStatusFilter(STATUS_FILTERS.ALL)}
            title="לחץ לצפייה בכל הפריטים"
          >
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">סה"כ פריטים</div>
          </div>
          <div
            className={`stat-card clickable-stat${
              statusFilter === STATUS_FILTERS.AVAILABLE
                ? " stat-card--active"
                : ""
            }`}
            onClick={() => setStatusFilter(STATUS_FILTERS.AVAILABLE)}
            title="לחץ לצפייה בפריטים זמינים"
          >
            <div className="stat-number">{stats.available}</div>
            <div className="stat-label">זמינים</div>
          </div>
          <div
            className={`stat-card clickable-stat${
              statusFilter === STATUS_FILTERS.ASSIGNED
                ? " stat-card--active"
                : ""
            }`}
            onClick={() => setStatusFilter(STATUS_FILTERS.ASSIGNED)}
            title="לחץ לצפייה בפריטים חתומים"
          >
            <div className="stat-number">{stats.assigned}</div>
            <div className="stat-label">חתום</div>
          </div>
          {!isChildUser && (
            <div
              className={`stat-card clickable-stat${
                statusFilter === STATUS_FILTERS.RETURNED
                  ? " stat-card--active"
                  : ""
              }`}
              onClick={() => setStatusFilter(STATUS_FILTERS.RETURNED)}
              title="לחץ לצפייה בפריטים שזוכו"
            >
              <div className="stat-number">{stats.returned}</div>
              <div className="stat-label">זוכו</div>
            </div>
          )}
          <div
            className={`stat-card tsav-stat-card${
              statusFilter === STATUS_FILTERS.SERIAL_ONLY
                ? " stat-card--active"
                : ""
            }`}
          >
            <div
              className="tsav-stat-main clickable-stat"
              onClick={() => setStatusFilter(STATUS_FILTERS.SERIAL_ONLY)}
              title="לחץ לצפייה בדוח צ"
            >
              <div className="tsav-label-settings-row">
                <span className="stat-label">דוח צ</span>
                {isAdmin && (
                  <button
                    type="button"
                    className="seen-settings-btn-stat"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSeenSettingsModal(true);
                    }}
                    title="הגדרות דוח צ"
                  >
                    <FaCog />
                  </button>
                )}
              </div>
              <div className="stat-number tsav-stat-number">
                {isChildUser
                  ? stats.serialTotal
                  : `${stats.serialTotal} / ${stats.serialSeenIn2Days}`}
              </div>
            </div>
          </div>
          {!isChildUser && (
            <div
              className={`stat-card clickable-stat not-seen-stat-card${
                statusFilter === STATUS_FILTERS.NOT_SEEN
                  ? " stat-card--active"
                  : ""
              }`}
              onClick={() => setStatusFilter(STATUS_FILTERS.NOT_SEEN)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setStatusFilter(STATUS_FILTERS.NOT_SEEN);
                }
              }}
              role="button"
              tabIndex={0}
              title="לחץ לצפייה בדוח צ — פריטים שלא סומנו (כמו בסינון)"
            >
              <div className="stat-number">{notSeenSerialCount}</div>
              <div className="stat-label not-seen-stat-label">
                דוח צ פריטים שלא סומנו
              </div>
            </div>
          )}
        </div>

        {/* Mobile: primary add — full width after stats, before search (clearer than beside list) */}
        {isMobile && !isChildUser && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {canScan && (
              <button
                className="inventory-scan-icon-btn"
                onClick={() => setShowScanModal(true)}
                disabled={operationLoading}
                title="סרוק תמונה / קבלה"
              >
                <FaCamera />
              </button>
            )}
            <button
              type="button"
              className="add-item-btn mobile-add-btn"
              onClick={() => {
                setEditingItem(null);
                setStockFormPrefill(null);
                setShowForm(true);
              }}
              disabled={operationLoading}
              style={{ flex: 1 }}
            >
              {operationLoading ? "מעבד..." : "הוסף פריט"}
            </button>
          </div>
        )}

        {/* RTL: חיפוש | יחידות — סינון סטטוס רק מכרטיסי הסטטיסטיקות למעלה */}
        <div className="inventory-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="חיפוש פריטים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="unit-filter-container">
            <InventoryUnitMultiSelect
              units={units}
              selectedIds={inventoryUnitFilterIds}
              onChange={setInventoryUnitFilterIds}
            />
          </div>
        </div>

        {isMobile && (
          <div
            className="inventory-items-count-bar inventory-items-count-bar-with-toggle"
            role="status"
          >
            <span className="inventory-items-count-text">
              מוצגים {displayedInventory.length} פריטים
            </span>
            <button
              type="button"
              className="inventory-serial-view-toggle"
              onClick={toggleInventorySerialViewMode}
              aria-pressed={inventorySerialViewMode === "grouped"}
              title={
                inventorySerialViewMode === "grouped"
                  ? "הצג כל פריט סידורי בשורה נפרדת"
                  : "קבץ פריטי סידורי לפי סוג (כמו פריט ללא סידורי)"
              }
            >
              {inventorySerialViewMode === "grouped" ? (
                <FaList aria-hidden />
              ) : (
                <FaLayerGroup aria-hidden />
              )}
            </button>
          </div>
        )}

        {/* Inventory Table - Conditional Rendering: Mobile Cards vs Desktop Table */}
        {/* Mobile view: Virtual scrolling - only renders visible cards for efficiency */}
        {isMobile ? (
          <>
            <div
              ref={setMobileContainerCallback}
              className="inventory-mobile-cards-container"
              style={{
                height: mobileContainerHeight,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <div
                style={{
                  height: mobileTotalHeight,
                  position: "relative",
                }}
              >
                <div
                  className="inventory-mobile-cards"
                  style={{
                    transform: `translateY(${mobileOffsetY}px)`,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                  }}
                >
                  {mobileVisibleItems.map((item, index) => (
                    <div
                      key={item.id}
                      ref={
                        index === 0 && mobileVisibleItems.length > 0 && isMobile
                          ? mobileCardHeightRef
                          : null
                      } // Measure first visible card
                    >
                      <MobileInventoryCard
                        item={item}
                        hideGroupedColumns={
                          inventorySerialViewMode === "grouped"
                        }
                        seenSettings={seenSettings}
                        isChildUser={isChildUser}
                        childUserPersonalNumber={childUserPersonalNumber}
                        userPermissions={userPermissions}
                        handleEdit={handleEdit}
                        handleReturn={handleReturn}
                        handleMakeAvailable={handleMakeAvailable}
                        handleDelete={handleDelete}
                        handleConfirmSeen={handleConfirmSeen}
                        resolveAssignedByName={resolveAssignedByName}
                        onItemNameClick={handleShowDistribution}
                        onAssignedNameClick={handleAssignedNameClick}
                        nicknameByPersonalNumber={nicknameByPersonalNumber}
                        personnel={personnel}
                        personnelUnitMap={personnelUnitMap}
                        units={units}
                        onUpdateNote={(itemId, personalNumber, note) =>
                          handleUpdateNote(
                            itemId,
                            personalNumber,
                            isChildUser ? "second" : "first",
                            note,
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : shouldUseVirtualScroll ? (
          /* Desktop: Virtual scroll for large datasets */
          <VirtualInventoryTable
            items={displayedInventory}
            disableSort={inventorySerialViewMode === "grouped"}
            hideGroupedColumns={inventorySerialViewMode === "grouped"}
            serialViewMode={inventorySerialViewMode}
            onToggleSerialViewMode={toggleInventorySerialViewMode}
            getUnitName={getUnitName}
            personnelUnitMap={personnelUnitMap}
            handleEdit={handleEdit}
            handleReturn={handleReturn}
            handleMakeAvailable={handleMakeAvailable}
            handleDelete={handleDelete}
            handleConfirmSeen={handleConfirmSeen}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            userPermissions={userPermissions}
            userRole={userRole}
            childUserUnitId={childUserUnitId}
            childUserPersonalNumber={childUserPersonalNumber}
            seenSettings={seenSettings}
            resolveAssignedByName={resolveAssignedByName}
            onItemNameClick={handleShowDistribution}
            onAssignedNameClick={handleAssignedNameClick}
            onUpdateNote={handleUpdateNote}
            scrollContainerRef={desktopVirtualScrollContainerRef}
            virtualScrollApiRef={virtualScrollApiRef}
            nicknameByPersonalNumber={nicknameByPersonalNumber}
            personnel={personnel}
          />
        ) : (
          <div
            className="inventory-table-container"
            ref={paginatedTableScrollRef}
          >
            <div
              className="inventory-table-items-count inventory-table-items-count-with-toggle"
              role="status"
            >
              <span className="inventory-items-count-text">
                {totalPages > 1 ? (
                  <>
                    מוצגים {paginatedInventory.length} מתוך{" "}
                    {displayedInventory.length} פריטים
                  </>
                ) : (
                  <>מוצגים {displayedInventory.length} פריטים</>
                )}
              </span>
              <button
                type="button"
                className="inventory-serial-view-toggle"
                onClick={toggleInventorySerialViewMode}
                aria-pressed={inventorySerialViewMode === "grouped"}
                title={
                  inventorySerialViewMode === "grouped"
                    ? "הצג כל פריט סידורי בשורה נפרדת"
                    : "קבץ פריטי סידורי לפי סוג (כמו פריט ללא סידורי)"
                }
              >
                {inventorySerialViewMode === "grouped" ? (
                  <FaList aria-hidden />
                ) : (
                  <FaLayerGroup aria-hidden />
                )}
              </button>
            </div>
            <table
              className={`inventory-table${
                inventorySerialViewMode === "grouped"
                  ? " inventory-table-sort-disabled inventory-table-grouped-columns"
                  : ""
              }`}
            >
              <thead>
                <tr>
                  <th
                    className="sortable"
                    onClick={() => handleSort("itemName")}
                    title="לחץ למיון"
                  >
                    שם פריט
                    {sortField === "itemName" && (
                      <span className="sort-indicator">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort("serialNumber")}
                    title="לחץ למיון"
                  >
                    {inventorySerialViewMode === "grouped"
                      ? "סטטוס"
                      : "מספר סידורי"}
                    {sortField === "serialNumber" && (
                      <span className="sort-indicator">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort("isOrganic")}
                    title="לחץ למיון"
                  >
                    סוג
                    {sortField === "isOrganic" && (
                      <span className="sort-indicator">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort("quantity")}
                    title="לחץ למיון"
                  >
                    כמות כללית
                    {sortField === "quantity" && (
                      <span className="sort-indicator">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th>כמות מוקצית</th>
                  <th>כמות זמינה</th>
                  {inventorySerialViewMode !== "grouped" && (
                    <>
                      <th
                        className="sortable"
                        onClick={() => handleSort("status")}
                        title="לחץ למיון"
                      >
                        סטטוס
                        {sortField === "status" && (
                          <span className="sort-indicator">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("assignedTo")}
                        title="לחץ: א-ת · זמינים קודם · ת-א"
                      >
                        חתום ל
                        {sortField === "assignedTo" && (
                          <span className="sort-indicator">
                            {sortDirection === "asc"
                              ? "↑"
                              : sortDirection === ASSIGNED_TO_SORT_AVAIL
                                ? "·"
                                : "↓"}
                          </span>
                        )}
                      </th>
                      {!isChildUser && <th>הוקצה על ידי</th>}
                      <th
                        className="sortable"
                        onClick={() => handleSort("currentUnitId")}
                        title="לחץ למיון"
                      >
                        יחידה
                        {sortField === "currentUnitId" && (
                          <span className="sort-indicator">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>
                      <th>מיקום/הערה</th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("seenAt")}
                        title="לחץ למיון לפי ימים מאז דוח צ"
                      >
                        דוח צ
                        {sortField === "seenAt" && (
                          <span className="sort-indicator">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>
                      <th>פעולות</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedInventory.map((item) => (
                  <InventoryRow
                    key={item.id}
                    item={item}
                    hideGroupedColumns={
                      inventorySerialViewMode === "grouped"
                    }
                    getUnitName={getUnitName}
                    personnelUnitMap={personnelUnitMap}
                    handleEdit={handleEdit}
                    handleReturn={handleReturn}
                    handleMakeAvailable={handleMakeAvailable}
                    handleDelete={handleDelete}
                    handleConfirmSeen={handleConfirmSeen}
                    userPermissions={userPermissions}
                    userRole={userRole}
                    childUserUnitId={childUserUnitId}
                    childUserPersonalNumber={childUserPersonalNumber}
                    resolveAssignedByName={resolveAssignedByName}
                    onItemNameClick={handleShowDistribution}
                    onAssignedNameClick={handleAssignedNameClick}
                    onUpdateNote={handleUpdateNote}
                    editingNoteId={paginatedTableNoteEditId}
                    setEditingNoteId={setPaginatedTableNoteEditId}
                    noteValue={paginatedTableNoteValue}
                    setNoteValue={setPaginatedTableNoteValue}
                    editingNoteMeta={paginatedTableNoteMeta}
                    setEditingNoteMeta={setPaginatedTableNoteMeta}
                    seenSettings={seenSettings}
                    nicknameByPersonalNumber={nicknameByPersonalNumber}
                    personnel={personnel}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls - Only for desktop regular table (mobile uses virtual scroll) */}
        {!isMobile && !shouldUseVirtualScroll && totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              מציג {startIndex + 1}-
              {Math.min(endIndex, displayedInventory.length)} מתוך{" "}
              {displayedInventory.length} פריטים
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ראשון
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                קודם
              </button>
              <span className="pagination-page">
                עמוד {currentPage} מתוך {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                הבא
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                אחרון
              </button>
            </div>
          </div>
        )}

        {/* Return Quantity Modal */}
        {showReturnModal && returnItemData && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>זיכוי פריט</h3>
                <button
                  className="modal-close"
                  onClick={handleReturnCancel}
                  title="סגור"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>פריט:</strong> {returnItemData.itemName}
                </p>
                <p>
                  <strong>כמות זמינה:</strong>{" "}
                  {returnItemData.availableQuantity ??
                    returnItemData.quantity ??
                    0}
                </p>
                <div className="form-group">
                  <label htmlFor="returnQuantity">כמות לזיכוי:</label>
                  <input
                    id="returnQuantity"
                    type="number"
                    min="1"
                    max={
                      returnItemData.availableQuantity ??
                      returnItemData.quantity ??
                      0
                    }
                    value={returnQuantity}
                    onChange={(e) =>
                      setReturnQuantity(parseInt(e.target.value) || 1)
                    }
                    onFocus={(e) => e.target.select()}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={handleReturnCancel}
                >
                  ביטול
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleReturnConfirm}
                  disabled={
                    returnQuantity <= 0 ||
                    returnQuantity >
                      (returnItemData.availableQuantity ??
                        returnItemData.quantity ??
                        0)
                  }
                >
                  אישור זיכוי
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <InventoryForm
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingItem(null);
              setStockFormPrefill(null);
            }}
            initialData={editingItem || stockFormPrefill}
          />
        )}

        {showScanModal && (
          <ImageScanModal
            isOpen={showScanModal}
            onClose={() => setShowScanModal(false)}
            onItemsConfirmed={(scannedItems) => {
              scannedItems.forEach((item) => {
                handleFormSubmit({
                  itemDefinitionId: "",
                  itemName: item.itemName,
                  quantity: item.quantity || 1,
                  serialNumber: "",
                  isOrganic: item.isOrganic ?? false,
                  currentUnitId: "",
                  status: "available",
                  createdBy: "system",
                  makat: item.makat || "",
                  note: item.note || "",
                  validateSerialNumber: false,
                  validateQuantityMerge: true,
                  isEdit: false,
                });
              });
              setShowScanModal(false);
            }}
          />
        )}

        {/* Seen Settings Modal */}
        {showSeenSettingsModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowSeenSettingsModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>הגדרות דוח צ</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowSeenSettingsModal(false)}
                  title="סגור"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="greenMax">
                    ימים לנראה (ירוק): 0 עד
                    <input
                      id="greenMax"
                      type="number"
                      min="0"
                      max="30"
                      value={seenSettings.greenMax}
                      onChange={(e) =>
                        setSeenSettings({
                          ...seenSettings,
                          greenMax: parseInt(e.target.value) || 0,
                        })
                      }
                      className="form-input"
                      style={{ width: "80px", margin: "0 8px" }}
                    />
                    ימים
                  </label>
                </div>
                <div className="form-group">
                  <label htmlFor="yellowDays">
                    ימים לצהוב:
                    <input
                      id="yellowDays"
                      type="number"
                      min="0"
                      max="30"
                      value={seenSettings.yellowDays}
                      onChange={(e) =>
                        setSeenSettings({
                          ...seenSettings,
                          yellowDays: parseInt(e.target.value) || 0,
                        })
                      }
                      className="form-input"
                      style={{ width: "80px", margin: "0 8px" }}
                    />
                    ימים (מעל זה אדום)
                  </label>
                </div>
                <div className="settings-info">
                  <p>
                    <strong>הסבר:</strong>
                  </p>
                  <ul>
                    <li>
                      <span
                        className="seen-green"
                        style={{ padding: "2px 8px" }}
                      >
                        ירוק
                      </span>{" "}
                      - 0 עד {seenSettings.greenMax} ימים
                    </li>
                    <li>
                      <span
                        className="seen-yellow"
                        style={{ padding: "2px 8px" }}
                      >
                        צהוב
                      </span>{" "}
                      - {seenSettings.yellowDays} ימים
                    </li>
                    <li>
                      <span className="seen-red" style={{ padding: "2px 8px" }}>
                        אדום
                      </span>{" "}
                      - מעל {seenSettings.yellowDays} ימים
                    </li>
                    <li>
                      כרטיס &quot;דוח צ פריטים שלא סומנו&quot;: פריטים בלי דיווח
                      (אין תאריך) או באדום בלבד — לא בצהוב; לפי אותם ספים כאן.
                    </li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSeenSettingsModal(false)}
                >
                  ביטול
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    localStorage.setItem(
                      "seenSettings",
                      JSON.stringify(seenSettings),
                    );
                    setShowSeenSettingsModal(false);
                  }}
                >
                  שמור
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Distribution Modal */}
        {distributionModal.show &&
          distributionModal.item &&
          distributionData && (
            <div className="modal-overlay" onClick={handleCloseDistribution}>
              <div
                className="modal-content distribution-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>התפלגות הפיזור - {distributionModal.item.itemName}</h3>
                  <button
                    className="modal-close"
                    onClick={handleCloseDistribution}
                    title="סגור"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="distribution-summary">
                    <span>
                      סה"כ: <strong>{distributionData.totalQuantity}</strong>
                    </span>
                    <span>
                      מוחתם: <strong>{distributionData.totalAssigned}</strong>
                    </span>
                    <span>
                      זמין: <strong>{distributionData.totalAvailable}</strong>
                    </span>
                  </div>

                  {!isChildUser && (
                    <div className="distribution-toggle">
                      <button
                        className={`distribution-toggle-btn ${
                          distributionModal.view === "customer" ? "active" : ""
                        }`}
                        onClick={() =>
                          setDistributionModal((prev) => ({
                            ...prev,
                            view: "customer",
                          }))
                        }
                      >
                        לפי חיילים
                      </button>
                      <button
                        className={`distribution-toggle-btn ${
                          distributionModal.view === "unit" ? "active" : ""
                        }`}
                        onClick={() =>
                          setDistributionModal((prev) => ({
                            ...prev,
                            view: "unit",
                          }))
                        }
                      >
                        לפי פלוגות
                      </button>
                    </div>
                  )}

                  {isChildUser || distributionModal.view === "customer" ? (
                    <table className="distribution-table">
                      <thead>
                        <tr>
                          {!isChildUser && <th>פלוגה</th>}
                          <th>חייל</th>
                          <th>כמות</th>
                          {distributionData.showSerialColumnCustomer ? (
                            <th>מספר סידורי</th>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {distributionData.byCustomer.length > 0 ? (
                          distributionData.byCustomer.map((entry, idx) => (
                            <tr key={idx}>
                              {!isChildUser && <td>{entry.unitName}</td>}
                              <td>
                                <span
                                  className={
                                    suppressDistributionCustomerNameClick
                                      ? undefined
                                      : "clickable-assigned-name"
                                  }
                                  onClick={
                                    suppressDistributionCustomerNameClick
                                      ? undefined
                                      : () =>
                                          handleDistributionNameClick(entry)
                                  }
                                >
                                  {entry.name}
                                </span>
                              </td>
                              <td>{entry.quantity}</td>
                              {distributionData.showSerialColumnCustomer ? (
                                <td>{entry.serialsLabel || "—"}</td>
                              ) : null}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={
                                (isChildUser ? 2 : 3) +
                                (distributionData.showSerialColumnCustomer
                                  ? 1
                                  : 0)
                              }
                              style={{ textAlign: "center" }}
                            >
                              אין הקצאות
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="distribution-table">
                      <thead>
                        <tr>
                          <th>פלוגה</th>
                          <th>כמות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {distributionData.byUnit.length > 0 ? (
                          distributionData.byUnit.map((entry, idx) => (
                            <tr key={idx}>
                              <td>{entry.name}</td>
                              <td>{entry.quantity}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" style={{ textAlign: "center" }}>
                              אין הקצאות
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

        {refundModalData && (
          <RefundModal
            isOpen={!!refundModalData}
            onClose={() => setRefundModalData(null)}
            inventoryItem={refundModalData.item}
            selectedEmployee={refundModalData.employee}
            inventory={inventory}
            items={itemsSettings}
            personnel={personnel}
            units={units}
            user={user}
            onRefresh={refreshInventory}
          />
        )}
      </div>
    </div>
  );
};

export default Inventory;
